import React from 'react';
import { BadgeCheckIcon, DownloadIcon, EyeIcon, PrinterIcon, StarIcon, XIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ReportControls } from '../trust/ReportControls';
import { StandingChip } from '../verification/StandingChip';
import { standingFor } from '../../data/standings';
import { naira } from '../../utils/format';
import type { Entitlement, KnowledgeResource } from '../../types/marketplace';
interface ResourceCardProps {
  resource: KnowledgeResource;
  entitlement?: Entitlement;
}
function Permission({
  allowed,
  label,
  icon
}: {
  allowed: boolean;
  label: string;
  icon: React.ReactNode;
}) {
  return <span className={`inline-flex items-center gap-1 text-[12px] ${allowed ? 'text-ink-muted' : 'text-ink-subtle line-through'}`} data-id="element-3008" data-mp="1ll4pksz5mgtt">
      {allowed ? icon : <XIcon className="h-3 w-3" data-id="element-3009" data-mp="2x0tc5j8hxdu3" />}
      {label}
    </span>;
}
export function ResourceCard({
  resource,
  entitlement
}: ResourceCardProps) {
  return <article className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-3010" data-mp="2kpel8s17hxed">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-3011" data-mp="2pnzn0vw3wp4x">
        <div className="min-w-0" data-id="element-3012" data-mp="2uodlpnbtimqa">
          <div className="flex flex-wrap items-center gap-2" data-id="element-3013" data-mp="2upf8gp274xnu">
            <h3 className="text-[15px] font-semibold text-ink" data-id="element-3014" data-mp="2zmx6vlil7uhl">{resource.title}</h3>
            <Badge data-id="element-3015" data-mp="21zgeqgd24ccu">{resource.kind}</Badge>
            {resource.authorVerified ? <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-3017" data-mp="26y9tec42y9el" />} data-id="element-3016" data-mp="21xc31c8x2920">
                Verified author
              </Badge> : <Badge tone="warn" data-id="element-3018" data-mp="2bwl2gkmjwfvr">Unverified author</Badge>}
          </div>
          <p className="mt-1 text-[13px] text-ink-muted" data-id="element-3019" data-mp="2gokwxxjuqgky">
            {resource.author}
            {resource.publisher ? ` · ${resource.publisher}` : ''} · {resource.edition}
          </p>
          {/* An author's academic standing, where they have one on file. It is
              not a statement about the quality of the resource. */}
          <div className="mt-1.5" data-id="element-3020" data-mp="1g5j3cg8kkqzs">
            <StandingChip standing={standingFor(resource.author)} data-id="element-3021" data-mp="1b894yb37d4zt" />
          </div>
          <p className="cz-num mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ink-muted" data-id="element-3022" data-mp="1b6euxrtgyxai">
            <span data-id="element-3023" data-mp="1ky2ez7np5c1y">{resource.field}</span>
            <span data-id="element-3024" data-mp="1kz3t5l00eenw">{resource.pagesOrLength}</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-3025" data-mp="1l0ey3o56xsgl">
              <StarIcon className="h-3.5 w-3.5" data-id="element-3026" data-mp="x900u9s9u1c7" />
              {resource.rating.toFixed(1)} ({resource.reviewCount})
            </span>
          </p>
        </div>
        {entitlement && <div className="shrink-0 text-right" data-id="element-3027" data-mp="177film5ery91">
            <Badge tone={entitlement.state === 'active' ? 'ok' : 'warn'} data-id="element-3028" data-mp="127kq5e5zj0lv">
              {entitlement.state === 'active' ? 'You have access' : 'Access expired'}
            </Badge>
            <p className="cz-num mt-1 text-[12px] text-ink-subtle" data-id="element-3029" data-mp="2inxdvix9ruu3">
              {entitlement.expiresOn ? entitlement.state === 'active' ? `Until ${entitlement.expiresOn}` : `Expired ${entitlement.expiresOn}` : 'No expiry'}
            </p>
          </div>}
      </div>

      <ul className="mt-4 space-y-2 border-t border-line pt-3" data-id="element-3030" data-mp="2c6ce2rawlojf">
        {resource.licences.map(licence => <li key={licence.model} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line px-3 py-2.5" data-id="element-3031" data-mp="2g87c9mxjflsz">
            <div className="min-w-0" data-id="element-3032" data-mp="38wczcgc2dmvz">
              <p className="text-[13px] font-medium text-ink" data-id="element-3033" data-mp="38vbni9n84wd7">
                {licence.model === 'free' ? 'Free access' : licence.model === 'rental' ? `Rental — ${licence.rentalDays} days` : 'Purchase'}
                {licence.priceNaira > 0 && <span className="cz-num ml-2 font-semibold" data-id="element-3034" data-mp="3mti50f7v8jqp">{naira(licence.priceNaira)}</span>}
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-3035" data-mp="23yj23c2xqhhv">
                <Permission allowed={licence.preview} label="Preview" icon={<EyeIcon className="h-3 w-3" data-id="element-3037" data-mp="1yxbubd0slib7" />} data-id="element-3036" data-mp="1yywp93r0r39l" />
                <Permission allowed={licence.download} label="Download" icon={<DownloadIcon className="h-3 w-3" data-id="element-3039" data-mp="1u03l41xmgom3" />} data-id="element-3038" data-mp="1tzk2sk427y7j" />
                <Permission allowed={licence.print} label="Print" icon={<PrinterIcon className="h-3 w-3" data-id="element-3041" data-mp="1q0b739pglq9c" />} data-id="element-3040" data-mp="1pz07lm31r4o1" />
                <span className="text-[12px] text-ink-subtle" data-id="element-3042" data-mp="2mnfypmlt1m39">{licence.territories}</span>
              </p>
            </div>
            <Button size="sm" variant={licence.model === 'free' ? 'secondary' : 'primary'} data-id="element-3043" data-mp="2hpxv8bqk3z2n">
              {licence.model === 'free' ? 'Open' : licence.model === 'rental' ? 'Rent' : 'Buy'}
            </Button>
          </li>)}
      </ul>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3" data-id="element-3044" data-mp="35h3c8u88gxej">
        <p className="text-[12px] leading-5 text-ink-subtle" data-id="element-3045" data-mp="30m5xr7s6pvu9">
          Paying records a transaction; access is governed separately by your entitlement and its
          licence period. Redistribution is never permitted.
        </p>
        <ReportControls subject={resource.title} surface="listing" data-id="element-3046" data-mp="w5sx34arq74d" />
      </div>
    </article>;
}